import { createContext, useCallback, useContext, useMemo } from 'react';
import type { PropsWithChildren, ReactElement } from 'react';

import { useLocalStorage } from 'usehooks-ts';

enum SortingDirections {
    asc = 'ASC',
    desc = 'DESC',
}

interface SortingState {
    id: string;
    direction: SortingDirections;
}
interface SortingProviderProps {
    /** An array of sorting IDs that the provider will manage */
    sortingIds: Array<string>;
    /** A unique key to store the sorting state in local storage */
    storageKey: string;
    /** Initial sorting state, if any. If not provided, the sorting will start as null */
    initialSorting?: SortingState | null;
}

interface SortingContextType {
    /** The current sorting state object */
    currentSorting: SortingState | null;
    /** Function to toggle the sorting direction */
    toggleSorting: (
        /** The ID of the sorting column, must match one of the sortingIds */
        sortingId: SortingState['id'],
        /** Optional direction to set, if not provided, it will toggle the current direction if left unspecified. Default will be desc.*/
        direction?: SortingState['direction']
    ) => void;
    /** Function to clear the sorting state */
    clearSorting: () => void;
}

const SortingContext = createContext<SortingContextType | null>(null);

const useSorting = (): SortingContextType => {
    const context = useContext(SortingContext);
    if (!context)
        throw new Error('useSorting must be used within SortingProvider');
    return context;
};

const SortingProvider = ({
    children,
    sortingIds,
    storageKey,
    initialSorting = null,
}: PropsWithChildren<SortingProviderProps>): ReactElement => {
    const [sortingState, setSortingState] =
        useLocalStorage<SortingState | null>(storageKey, initialSorting);

    const toggleSorting = useCallback(
        (
            sortingId: SortingState['id'],
            direction?: SortingState['direction']
        ): void => {
            // Check if the sortingId is included in the provided sortingIds
            if (!sortingIds.includes(sortingId)) {
                console.warn(
                    `Sorting ID "${sortingId}" is not included in the provided sortingIds: ${sortingIds.join(', ')}`
                );
                return;
            }

            setSortingState((prevState) => {
                // If no direction is provided, toggle the current direction
                if (!direction) {
                    // If the previous state is null, we set the initial sorting state
                    if (prevState?.id === sortingId) {
                        return {
                            id: sortingId,
                            direction:
                                prevState.direction === SortingDirections.asc
                                    ? SortingDirections.desc
                                    : SortingDirections.asc,
                        };
                    }

                    // Else we set the default direction to asc (initial click on a new column)
                    else {
                        return {
                            id: sortingId,
                            direction: SortingDirections.asc,
                        };
                    }
                }

                // If a direction is provided, set it directly
                return {
                    id: sortingId,
                    direction,
                };
            });
        },
        [sortingIds, setSortingState]
    );

    const contextValue = useMemo(
        () => ({
            currentSorting: sortingState,
            toggleSorting,
            clearSorting: (): void => {
                setSortingState(null);
            },
        }),
        [setSortingState, sortingState, toggleSorting]
    );

    return (
        <SortingContext.Provider value={contextValue}>
            {children}
        </SortingContext.Provider>
    );
};

export { SortingProvider, useSorting, SortingDirections };
export type { SortingState };
